<script setup lang="ts">
import { computed } from 'vue';
import { useQuasar } from 'quasar';
import useDisplayPdf from '@/composables/displayPdf';
import FlightSchoolRepository from '@/repositories/FlightSchoolRepository';
import type { PrintableCourseStudent } from '@/types/FlightSchoolStudent';
import { StudentCourseStatus } from '@/types/FlightSchoolStudent';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';

const props = defineProps<{
    currentSchool: FlightSchool,
    currentCourse: FlightSchoolCourse,
    printableStudent: PrintableCourseStudent,
    certificateType: 'enrollment'|'graduation',
}>();

const $q = useQuasar();
const showLoading = (certificate: string) => {
    $q.loading.show({
        message: `Loading ${certificate} certificate. Please wait...`,
    });
};

const hideLoading = () => {
    $q.loading.hide();
};

// Part 61 students aren't given an enrollment certificate.
const isPart61 = computed(() => props.currentCourse.certificate.part === 61);

const getEnrollmentCertificate = async () => {
    showLoading('Enrollment');
    const pdf = await FlightSchoolRepository
        .getStudentEnrollmentCertificate(
            props.currentSchool,
            props.currentCourse,
            props.printableStudent,
        );
    hideLoading();

    // necessary to wait for the loading to hide before displaying the pdf
    setTimeout(() => {
        useDisplayPdf(pdf, 'Enrollment Certificate');
    }, 100);
};

const getGraduationCertificate = async () => {
    showLoading('Graduation');
    const pdf = await FlightSchoolRepository
        .getStudentGraduationCertificate(
            props.currentSchool,
            props.currentCourse,
            props.printableStudent,
        );
    hideLoading();
    setTimeout(() => {
        useDisplayPdf(pdf, 'Graduation Certificate');
    }, 100);
};

/**
 * Student has graduated if their course status is 'graduated'.
 */
const studentHasGraduated = computed(() => props.printableStudent.courseStatus === StudentCourseStatus.graduated);
</script>
<template>
    <template v-if="certificateType === 'enrollment'">
        <q-btn
            id="enrollment-certificate"
            label="View Enrollment Certificate"
            class="anchor-button text-body2"
            color="fsum-med-blue"
            :ripple="false"
            no-caps
            dense
            flat
            :disable="isPart61"
            @click="getEnrollmentCertificate"
        />
    </template>
    <template v-else>
        <q-btn
            id="graduation-certificate"
            label="View Graduation Certificate"
            class="anchor-button text-body2"
            color="fsum-med-blue"
            :ripple="false"
            no-caps
            dense
            flat
            :disable="!studentHasGraduated"
            @click="getGraduationCertificate"
        />
    </template>
</template>
